// frontend/src/components/PayPalCheckout.js
/**
 * PayPal Checkout Component
 * Integrates PayPal JS SDK for gift payments (embedded flow).
 */

import React, { useState, useRef } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { SERVER_URL, PAYPAL_CLIENT_ID } from '../config';

/**
 * PayPal Checkout Component
 *
 * @param {Object} props
 * @param {number} props.amount - Gift amount
 * @param {number} props.tipAmount - Optional tip amount to the platform
 * @param {string} props.recipientName - Name of gift recipient
 * @param {string} props.recipientEmail - Email of gift recipient
 * @param {string} props.recipientMerchantId - Seller's PayPal payer ID (required by IWT)
 * @param {string} props.giverName - Name of gift giver
 * @param {string} props.giverEmail - Email of gift giver
 * @param {string} props.message - Gift message
 * @param {string} props.occasion - Gift occasion
 * @param {string} props.postId - Optional post ID for pool contributions
 * @param {Function} props.onSuccess - Callback on successful payment
 * @param {Function} props.onError - Callback on payment error
 */
const PayPalCheckout = ({
  amount,
  tipAmount = 0,
  recipientName,
  recipientEmail,
  recipientMerchantId = null,  // Seller's PayPal payer ID (required by IWT)
  giverName,
  giverEmail,
  message = '',
  occasion = 'Just Because',
  postId = null,
  onSuccess,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fundingSource, setFundingSource] = useState(null);
  const pendingGiftIdRef = useRef(null);

  // Detect if we're in sandbox/development mode
  const isSandbox = process.env.NODE_ENV === 'development' ||
    PAYPAL_CLIENT_ID?.includes('sandbox') ||
    PAYPAL_CLIENT_ID?.startsWith('sb-');

  // PayPal SDK options
  const initialOptions = {
    clientId: PAYPAL_CLIENT_ID,
    currency: "USD",
    intent: "capture",
    commit: true,  // Enables "Pay Now" experience (IWT requirement)
    enableFunding: "venmo",  // Show Venmo as payment option (US only)
    dataPartnerAttributionId: "GIFTED_SP_PPCP",
    dataPageType: "checkout",
    // Seller's PayPal payer ID — required by IWT for multiparty transactions
    ...(recipientMerchantId && { merchantId: recipientMerchantId }),
    // Required for Venmo testing in sandbox
    ...(isSandbox && { buyerCountry: "US" }),
  };

  /**
   * Create PayPal order on backend
   */
  const createOrder = async (data, actions) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${SERVER_URL}/paypal/create-gift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          giver_name: giverName,
          giver_email: giverEmail,
          recipient_name: recipientName,
          recipient_email: recipientEmail,
          amount: amount,
          tip_amount: tipAmount,
          message: message,
          occasion: occasion,
          post_id: postId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create order');
      }

      const orderData = await response.json();

      // Store gift ID in a component-local ref so concurrent checkouts in other
      // tabs don't clobber each other (sessionStorage is shared across tabs).
      pendingGiftIdRef.current = orderData.gift_id;

      return orderData.order_id;
    } catch (err) {
      console.error('Error creating PayPal order:', err);
      setError(err.message);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Capture payment after user approves
   */
  const onApprove = async (data, actions) => {
    setIsLoading(true);
    setError(null);

    const giftId = pendingGiftIdRef.current;

    try {
      const response = await fetch(
        `${SERVER_URL}/paypal/capture?gift_id=${giftId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // INSTRUMENT_DECLINED: restart the flow so buyer can pick another funding source
        if (
          errorData.detail?.includes('INSTRUMENT_DECLINED') ||
          errorData.detail?.includes('instrument_declined')
        ) {
          setIsLoading(false);
          return actions.restart();
        }
        throw new Error(errorData.detail || 'Failed to capture payment');
      }

      const captureData = await response.json();

      // Clear stored gift ID
      pendingGiftIdRef.current = null;

      if (onSuccess) {
        onSuccess({
          giftId: captureData.gift_id,
          amount: captureData.amount,
          orderID: data.orderID,
          fundingSource: fundingSource,
          buyerEmail: captureData.buyer_email,
        });
      }

      return captureData;
    } catch (err) {
      console.error('Error capturing PayPal payment:', err);
      setError(err.message);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle payment cancellation
   */
  const onCancel = (data) => {
    console.log('PayPal payment cancelled', data);
    setError('Payment was cancelled');
    pendingGiftIdRef.current = null;
  };

  /**
   * Handle payment error
   */
  const onErrorHandler = (err) => {
    console.error('PayPal payment error:', err);
    setError(err.message || 'An error occurred during payment');
    if (onError) onError(err);
  };

  return (
    <div className="paypal-checkout-container">
      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Payment Error:</strong> {error}
        </div>
      )}

      {isLoading && (
        <div className="text-center mb-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Processing payment...</span>
          </div>
          <p className="mt-2">Processing your payment...</p>
        </div>
      )}

      <PayPalScriptProvider options={initialOptions}>
        <PayPalButtons
          style={{
            layout: "vertical",
            color: "gold",
            shape: "rect",
            label: "paypal",
          }}
          appSwitchWhenAvailable={true}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={onCancel}
          onError={onErrorHandler}
          onClick={(data, actions) => {
            setFundingSource(data.fundingSource);
          }}
          disabled={isLoading}
          forceReRender={[amount, tipAmount, recipientEmail, giverEmail]}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckout;
